import {
  BarChart2,
  Bookmark,
  CheckSquare,
  CopyPlus,
  CreditCard,
  GraduationCap,
  LayoutGrid,
  ListChecks,
  LogIn,
  LogOut,
  MousePointer2,
  PlaySquare,
  RefreshCw,
  Settings2,
  Shapes,
  Square,
  Timer,
  ThumbsUp,
  Type,
  Volume2,
} from 'lucide-vue-next'
import type { FlowNode, PaletteGroup, PaletteItem } from './types'
import { reviewPalette } from '../../integrations/personalReview'

type Translate = (key: string) => string

/** Every node the palette offers, in display order. Ids are stable drag/drop keys. */
export function buildFlowPalette(t: Translate): PaletteItem[] {
  const session = t('flow.palette.categorySession')
  const macros = t('flow.palette.categoryMacros')
  const structure = t('flow.palette.categoryStructure')
  const cards = t('flow.palette.categoryCards')
  const flow = t('flow.palette.categoryFlow')
  const registers = t('flow.palette.categoryRegisters')
  const phase = t('flow.palette.categoryPhase')
  const media = t('flow.palette.categoryMedia')
  const item = (
    id: string,
    type: PaletteItem['type'],
    labelKey: string,
    icon: PaletteItem['icon'],
    category: string,
    extra: Partial<PaletteItem> = {},
  ): PaletteItem => ({
    id,
    type,
    label: t(`flow.palette.${labelKey}`),
    description: t(`flow.palette.${labelKey}Desc`),
    icon,
    category,
    ...extra,
  })

  return reviewPalette([
    item('start', 'start', 'sessionStart', LogIn, session, { singleton: true }),
    item('end', 'end', 'sessionEnd', Square, session, { singleton: true }),
    item('macro-start', 'macro-start', 'macroStart', CopyPlus, macros),
    item('macro-end', 'macro-end', 'macroEnd', LogOut, macros),
    item('macro-call', 'macro-call', 'macroCall', LayoutGrid, macros),
    item('group', 'group', 'groupGate', ListChecks, structure),
    item('condition', 'condition', 'condition', Shapes, structure),
    item('pull-next-card', 'action', 'pullCard', CreditCard, cards, {
      defaultLabel: 'Pull Next Card',
      defaultData: { actionType: 'pull-next-card' },
    }),
    item('click-checkbox', 'action', 'clickCheckbox', CheckSquare, cards, {
      defaultLabel: 'Click Checkbox',
      defaultData: { actionType: 'click-checkbox', checkboxCount: 1, checkboxDelayMs: 500 },
    }),
    item('unclick-checkbox', 'action', 'unclickCheckbox', CheckSquare, cards, {
      defaultLabel: 'Unclick Checkbox',
      defaultData: { actionType: 'unclick-checkbox', checkboxCount: 1, checkboxDelayMs: 500 },
    }),
    {
      // Not translated in the original UI.
      id: 'set-initial-checkboxes',
      type: 'action',
      label: 'Set Initial Checks',
      description: 'Pre-check N boxes on cards for this level.',
      icon: CheckSquare,
      category: cards,
      defaultLabel: 'Set Initial Checks',
      defaultData: { actionType: 'set-initial-checkboxes', checkboxCount: 0 },
    },
    item('rotate-card', 'action', 'flipCard', RefreshCw, cards, {
      defaultLabel: 'Rotate Card',
      defaultData: { actionType: 'rotate-card' },
    }),
    item('ask-known', 'action', 'askKnown', ThumbsUp, cards, {
      defaultLabel: 'Ask: known?',
      defaultData: { actionType: 'ask-known' },
    }),
    item('wait-for-user-action', 'action', 'waitClick', MousePointer2, flow, {
      defaultLabel: 'Wait for User',
      defaultData: { actionType: 'wait-for-user-action', waitDelayMs: 0 },
    }),
    item('delay', 'action', 'delay', Timer, flow, {
      defaultLabel: 'Delay',
      defaultData: { actionType: 'delay' },
    }),
    item('emit-complete', 'action', 'earlyExit', LogOut, flow, {
      defaultLabel: 'Emit Complete',
      defaultData: { actionType: 'emit-complete' },
    }),
    item('offer-practice', 'action', 'offerPractice', GraduationCap, flow, {
      defaultLabel: 'Offer Practice',
      defaultData: { actionType: 'offer-practice', trackId: 'practice' },
    }),
    item('play-audio', 'action', 'playAudio', Volume2, flow, {
      defaultLabel: 'Play Audio',
      defaultData: { actionType: 'play-audio', audioElementIndex: 0 },
    }),
    item('action', 'action', 'action', Settings2, flow),
    item('get-value', 'action', 'getValue', BarChart2, registers, {
      defaultLabel: 'Get Value',
      defaultData: { actionType: 'get-value', getSource: 'group-size', getRegister: 'R1' },
    }),
    item('set-value', 'action', 'setValue', Settings2, registers, {
      defaultLabel: 'Set Value',
      defaultData: { actionType: 'set-value', setTarget: 'progress-total', setInput: 'R1' },
    }),
    item('math-op', 'action', 'math', Type, registers, {
      defaultLabel: 'Math Op',
      defaultData: {
        actionType: 'math-op',
        mathTarget: 'R1',
        mathOp: '+1',
        mathOperand: 'literal',
        mathLit: 1,
      },
    }),
    item('phase', 'phase', 'phase', Bookmark, phase),
    item('phase-save', 'action', 'phaseSave', CheckSquare, phase, {
      defaultLabel: 'Save Progress',
      defaultData: { actionType: 'phase-save' },
    }),
    item('show-media', 'action', 'showMedia', PlaySquare, media, {
      defaultLabel: 'Show Media',
      defaultData: { actionType: 'show-media', mediaType: 'image', displayMode: 'standalone' },
    }),
  ])
}

/** Groups palette items by category, keeping first-seen category order. */
export function groupPalette(items: PaletteItem[], miscLabel: string): PaletteGroup[] {
  const map = new Map<string, PaletteItem[]>()
  for (const item of items) {
    const category = item.category ?? miscLabel
    const list = map.get(category) ?? []
    list.push(item)
    map.set(category, list)
  }
  return Array.from(map.entries()).map(([label, list]) => ({ label, items: list }))
}

/** Singleton items (start/end) are disabled once such a node exists. */
export function disabledPaletteIds(items: PaletteItem[], nodes: FlowNode[]) {
  return new Set(
    items
      .filter((item) => item.singleton && nodes.some((node) => node.type === item.type))
      .map((item) => item.id),
  )
}

/** Single-key shortcuts that create nodes at the cursor. */
export const PALETTE_SHORTCUTS: Record<string, string> = {
  p: 'phase',
  g: 'group',
  c: 'pull-next-card',
  f: 'rotate-card',
  d: 'delay',
  m: 'math-op',
  k: 'condition',
}
